import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { isBrowser } from "react-device-detect";
import { useSearchParams } from "react-router-dom";
import { getAPIAsync, postAPIAsync } from "../api/apiCalls";
import Provider from "../api/provider";
import { CHAT_FOOTER_START_HEIGHT, inIframe, isNullOrEmpty, SELLER, SELLER_SITE_PATH, WEBSITE_PATH, windowPostMessage } from "../common/common";
import { resource } from "../common/resource";
import ChatSkeleton, { ChatFooterSkeleton, ChatHeaderSkeleton } from "../components/skeletonLoaders/chatSkeleton";
import { ReceiverDetailsContext } from "../contexts/receiverDetailsContext";
import { Connection } from "../models/connector";
import { User } from "../models/messaging";
import Connector from "../signalr/signalr-connection";
import "../styles/chat.css";
import ChatWindow from "./chatWindow";

let senderProfileDetails: User | null = null,
  receiverProfileDetails: User | null = null,
  ucnum: string | null = null,
  senderID: string = "",
  receiverID: string = "";

const ChatiFrame = () => {
  const [searchParam] = useSearchParams();
  const [conversationID, setConversationID] = useState<string>("");
  const [messageConnection, setMessageConnection] = useState<Connection | undefined>();
  const [userConnection, setUserConnection] = useState<Connection | undefined>();
  const [receiverDetails, setReceiverDetails] = useState<User | null>(null);
  const from: string | undefined = searchParam.get("from");
  const receiverDetailsRef: any = useRef<User | null>(null);
  const chatID = searchParam.get("chatId");

  const updateStatus = (status: number) => {
    if (receiverDetails) {
      setReceiverDetails({ ...receiverDetails, followingStatus: status });
    }
  };
  const createConnection = () => {
    const connectingMessage: any = Connector(conversationID, () => {
      setMessageConnection(connectingMessage);
      setUserConnection(Connector(decodeURIComponent(receiverID)));
    });
  };
  const setDetails = () => {
    if (isNullOrEmpty(conversationID)) {
      getAPIAsync(
        Provider.URLS.BASE_URL,
        Provider.API_URLS.GetConversationID,
        { ucnum1: encodeURIComponent(senderID), ucnum2: encodeURIComponent(receiverID) },
        (response: any) => {
          if (!isNullOrEmpty(response.data.conversationID)) {
            setConversationID(response.data.conversationID);
            windowPostMessage({ type: "addChat", conversationID: response.data.conversationID, status: response.data.status, usercodenum: receiverID, decodedUcnum: chatID, sessionID: searchParam.get("sessionId"), userType: from === "thirdpartyStore" ? "store" : "user" }, WEBSITE_PATH);
          } else {
            const lastActivityTime: number = new Date().getTime();
            postAPIAsync(
              Provider.URLS.BASE_URL,
              Provider.API_URLS.InsertConversation,
              { ucnum1: decodeURIComponent(senderID), ucnum2: decodeURIComponent(receiverID), conversationID: "", isActive: true, createdAt: lastActivityTime, lastActiveAt: 0, status: from === "thirdpartyStore" ? 0 : 1, type: from === "thirdpartyStore" ? 2 : 1 },
              (response: any) => {
                setConversationID(response.data);
                windowPostMessage({ type: "addChat", conversationID: response.data, usercodenum: receiverID, decodedUcnum: chatID, sessionID: searchParam.get("sessionId"), userType: from === "thirdpartyStore" ? "store" : "user" }, WEBSITE_PATH);
              },
              () => {
                windowPostMessage({ type: "snackMsg", snackMsg: resource.error }, WEBSITE_PATH);
              }
            );
          }
        },
        () => {
          windowPostMessage({ type: "snackMsg", snackMsg: resource.error }, WEBSITE_PATH);
        }
      );
    }
  };
  const handleWindowMessageReceived = (event: any) => {
    const sitePath: string = SELLER ? SELLER_SITE_PATH : WEBSITE_PATH;
    if (event.origin && event.origin.includes(sitePath.slice(0, -1))) {
      const data = event.data;
      switch (data.type) {
        case "chatDetails":
          const chatDetails: any = data.chatDetails;
          if (chatDetails) {
            senderProfileDetails = JSON.parse(chatDetails.senderProfileDetails);
            receiverProfileDetails = JSON.parse(chatDetails.receiverProfileDetails);
            ucnum = JSON.parse(chatDetails.senderProfileDetails).ucnum;
            senderID = chatDetails.senderUserID;
            receiverID = chatDetails.receiveruserid;
            setConversationID(chatDetails.conversationID);
            setReceiverDetails(receiverProfileDetails);
          } else {
            setDetails();
          }
          break;
        case "receiverID":
          receiverID = data.receiverID;
          setDetails();
          break;
        case "updateFollowerCount":
          setReceiverDetails({ ...receiverDetailsRef.current, followers: data.followers });
          break;
      }
    }
  };

  useEffect(() => {
    if (!isNullOrEmpty(conversationID)) {
      createConnection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationID]);
  useEffect(() => {
    if (receiverDetails) {
      receiverDetailsRef.current = receiverDetails;
      windowPostMessage({ type: "updateReceiverDetails", chatID: chatID, receiverDetails: receiverDetails }, WEBSITE_PATH);
      windowPostMessage({ type: "updateFollowerCount", chatID: chatID, receiverDetails: receiverDetails }, WEBSITE_PATH);
      windowPostMessage({ type: "changeFollowStatus3rdParty", status: receiverDetails?.followingStatus, receiverID: receiverID }, WEBSITE_PATH);
      windowPostMessage({ type: "refresh" }, WEBSITE_PATH);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiverDetails]);
  useEffect(() => {
    if (!inIframe()) {
      window.location.href = WEBSITE_PATH;
      return;
    }
    if (!isNullOrEmpty(from) && (from === "thirdparty" || from === "thirdpartyStore")) {
      senderID = searchParam.get("userId");
      windowPostMessage({ type: "getReceiverID", chatID: chatID }, WEBSITE_PATH);
    } else {
      windowPostMessage({ type: "getChatDetails", chatID: chatID }, WEBSITE_PATH);
    }
    if (!isBrowser) {
      windowPostMessage({ type: "pushState", pushPage: "chat" }, WEBSITE_PATH);
    }
    window.addEventListener("message", handleWindowMessageReceived);

    // cleanup
    return () => {
      window.removeEventListener("message", handleWindowMessageReceived);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ReceiverDetailsContext.Provider value={{ receiverDetails, setReceiverDetails }}>
      <Box className="chatWindowHolder pos-abs w-per-100 h-per-100 pad-top-gutter" sx={{ bottom: 0 }}>
        {conversationID && receiverDetails != null && messageConnection && userConnection && senderProfileDetails && receiverProfileDetails && ucnum ? (
          <ChatWindow messageConnection={messageConnection} userConnection={userConnection} conversationID={conversationID} senderID={senderID} receiverID={receiverID} senderProfileDetails={senderProfileDetails} chatID={chatID} onFollowStatusUpdate={updateStatus} ucnum={ucnum} />
        ) : (
          <Box className={`chatHolder w-per-100 h-per-100 pos-rel`}>
            <Box className={`bg-true-white chatWindow`}>
              <ChatHeaderSkeleton />
              <ChatSkeleton chatAreaHeight={CHAT_FOOTER_START_HEIGHT} />
              <ChatFooterSkeleton />
            </Box>
          </Box>
        )}
      </Box>
    </ReceiverDetailsContext.Provider>
  );
};

export default ChatiFrame;
